import type { UpdateUserPayload } from "@/services/api/userProfile";
import {
  AUTH_ROUTE_PATHS,
  DEFAULT_AUTH_ROLE,
  JOURNEY_STEP_KEYS,
  getJourneyPath,
  type JourneyStepKey,
} from "./authConfig";

const SIGN_UP_JOURNEY_DRAFT_KEY = "bfc_sign_up_journey_draft";

export interface SignUpDraftFormData {
  companyName: string;
  companyWebsite: string;
  businessNeeds: string[];
  country: string;
  city: string;
  industryCategoryIds: string[];
  servicesOffered: string[];
  hobbies: string[];
  citiesTravelled: string[];
  gender: string;
  dob: string;
  instagramProfileUrl: string;
  linkedinProfileUrl: string;
  facebookProfileUrl: string;
  isPushNotificationEnabled: boolean;
  language: string;
  profileUrl: string;
}

export interface SignUpJourneyDraft {
  ownerEmail?: string;
  currentStepKey?: JourneyStepKey;
  formData: SignUpDraftFormData;
  accountCreated?: boolean;
  questionsCompleted?: boolean;
}

export const EMPTY_SIGN_UP_DRAFT_FORM_DATA: SignUpDraftFormData = {
  companyName: "",
  companyWebsite: "",
  businessNeeds: [],
  country: "",
  city: "",
  industryCategoryIds: [],
  servicesOffered: [],
  hobbies: [],
  citiesTravelled: [],
  gender: "",
  dob: "",
  instagramProfileUrl: "",
  linkedinProfileUrl: "",
  facebookProfileUrl: "",
  isPushNotificationEnabled: true,
  language: "en",
  profileUrl: "",
};

const isBrowser = () => typeof window !== "undefined";

export const saveSignUpJourneyDraft = (draft: SignUpJourneyDraft) => {
  if (!isBrowser()) {
    return;
  }

  localStorage.setItem(SIGN_UP_JOURNEY_DRAFT_KEY, JSON.stringify(draft));
};

export const getSignUpJourneyDraft = (
  ownerEmail?: string | null,
): SignUpJourneyDraft | null => {
  if (!isBrowser()) {
    return null;
  }

  const rawDraft = localStorage.getItem(SIGN_UP_JOURNEY_DRAFT_KEY);

  if (!rawDraft) {
    return null;
  }

  try {
    const parsedDraft = JSON.parse(rawDraft) as Partial<SignUpJourneyDraft> | null;

    if (!parsedDraft?.formData) {
      return null;
    }

    if (
      ownerEmail &&
      parsedDraft.ownerEmail &&
      parsedDraft.ownerEmail !== ownerEmail
    ) {
      return null;
    }

    return {
      ...(parsedDraft.ownerEmail !== undefined
        ? { ownerEmail: parsedDraft.ownerEmail }
        : {}),
      currentStepKey:
        parsedDraft.currentStepKey || JOURNEY_STEP_KEYS.personalInfo,
      accountCreated: Boolean(parsedDraft.accountCreated),
      questionsCompleted: Boolean(parsedDraft.questionsCompleted),
      formData: {
        ...EMPTY_SIGN_UP_DRAFT_FORM_DATA,
        ...parsedDraft.formData,
      },
    };
  } catch {
    clearSignUpJourneyDraft();
    return null;
  }
};

export const clearSignUpJourneyDraft = () => {
  if (!isBrowser()) {
    return;
  }

  localStorage.removeItem(SIGN_UP_JOURNEY_DRAFT_KEY);
};

const toNullableString = (value?: string) => {
  const trimmedValue = value?.trim();
  return trimmedValue ? trimmedValue : null;
};

const toNumberIfPossible = (value: string) => {
  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? value : parsedValue;
};

export const getPendingJourneyPath = (
  isProfileUpdated?: boolean | null,
  ownerEmail?: string | null,
) => {
  const draft = getSignUpJourneyDraft(ownerEmail);

  if (!isProfileUpdated) {
    return AUTH_ROUTE_PATHS.signUp;
  }

  if (!draft?.accountCreated) {
    return null;
  }

  if (
    draft.currentStepKey === JOURNEY_STEP_KEYS.introduction ||
    draft.questionsCompleted
  ) {
    return null;
  }

  if (draft.currentStepKey === JOURNEY_STEP_KEYS.questions) {
    return getJourneyPath(JOURNEY_STEP_KEYS.questions);
  }

  return getJourneyPath(JOURNEY_STEP_KEYS.welcome);
};

export const buildUpdateUserPayload = (
  draft: SignUpJourneyDraft,
): UpdateUserPayload => ({
  companyName: toNullableString(draft.formData.companyName),
  companyWebsite: toNullableString(draft.formData.companyWebsite),
  businessNeeds: draft.formData.businessNeeds,
  countryId: draft.formData.country
    ? toNumberIfPossible(draft.formData.country)
    : null,
  country: draft.formData.country
    ? toNumberIfPossible(draft.formData.country)
    : null,
  cityId: draft.formData.city
    ? toNumberIfPossible(draft.formData.city)
    : null,
  city: draft.formData.city
    ? toNumberIfPossible(draft.formData.city)
    : null,
  industryCategoryIds: draft.formData.industryCategoryIds.map(toNumberIfPossible),
  servicesOffered: draft.formData.servicesOffered,
  hobbies: draft.formData.hobbies,
  citiesTravelled: draft.formData.citiesTravelled.map(toNumberIfPossible),
  gender: toNullableString(draft.formData.gender),
  dob: toNullableString(draft.formData.dob),
  userType: DEFAULT_AUTH_ROLE,
  socialLink: {
    instagramProfileUrl: toNullableString(draft.formData.instagramProfileUrl),
    linkedinProfileUrl: toNullableString(draft.formData.linkedinProfileUrl),
    facebookProfileUrl: toNullableString(draft.formData.facebookProfileUrl),
  },
  userSettings: {
    isPushNotificationEnabled: draft.formData.isPushNotificationEnabled,
    language: toNullableString(draft.formData.language),
  },
  profileUrl: toNullableString(draft.formData.profileUrl),
});
